import { InjectionToken } from '@angular/core';
import type { Stripe, StripeConstructorOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

/** Loads Stripe.js for a publishable key. Mirrors the `loadStripe` signature. */
export type StripeLoader = (
  stripeKey: string,
  options?: StripeConstructorOptions,
) => Promise<Stripe | null>;

/**
 * Supplies the function that fetches Stripe.js. Defaults to the real
 * `loadStripe`, so **application code needs no provider** — inject the token
 * instead of importing `loadStripe` directly and production behavior is
 * identical.
 *
 * It exists so unit tests can swap in a loader that never reaches the network.
 * `loadStripe` appends a `<script src="https://js.stripe.com/v3">` to the
 * document; Zone counts that download as pending work, so a spec that awaits
 * `whenStable()` waits on a real network round trip and flakes when it is slow.
 * Specs call `provideStripeTestLoader()` from the `/testing` entry point to
 * replace the loader with one that never settles.
 *
 * Note the import above is `@stripe/stripe-js/pure`, not the package root. The
 * root entry appends that `<script>` from module top-level code as soon as it is
 * imported, whether or not anything calls `loadStripe`. An import cannot be
 * un-run by a provider, so importing the root here would inject the script into
 * every spec and make this token pointless. The `pure` entry has no import-time
 * side effect, which is what lets the override actually work.
 */
export const STRIPE_LOADER = new InjectionToken<StripeLoader>('Stripe.js loader', {
  factory: () => loadStripe,
});
